import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import { get, trim } from "lodash";
import {
  SdfAlert,
  SdfBox,
  SdfButton,
  SdfFocusPane,
  SdfInput,
  SdfSelect,
  SdfSelectSimple,
} from '@synerg/react-components'

export const ImportModal = (props = {}) => {
  const {
    project,
    setModalObj,
    setLoading,
    setShowToast,
    setError,
    setInfoMessage,
    reloadProjectList,
  } = props;

  const {
    grantTypeCode,
    ooid,
    projectName,
    consumerApplicationID,
    companionProjectName,
    companionAppID,
    appListingUrls,
  } = project;

  // USE STATE
  const [selectedValues, setSelectedValues] = useState({});
  const [applicationNameList, setApplicationNameList] = useState([]);
  const [listOfUSProducts, setListOfUSProducts] = useState([]);
  const [completeList, setCompleteList] = useState([]);

  // FORM
  const { control, handleSubmit, setValue, reset } = useForm({
    projectName: "",
    locale: {},
    product: {},
    type: {},
  });
  const other = {
    name: "Other",
    sizeName: "other",
    key: "other",
    label: "Other",
    value: "other",
  };
  const listOfLocales = [
    { label: "US", value: "US" },
    { label: "CA", value: "CA" },
  ];
  const listOfTypes = [
    { label: "DEV", value: "DEV" },
    { label: "PROD", value: "PROD" },
  ];

  const listOfDevProjects = [
    { name: "No Associate DEV Project", id: null },
  ].concat(get(completeList, "DEV", []));

  const listOfProdProjects = [
    { name: "No Associate PROD Project", id: null },
  ].concat(get(completeList, "PROD", []));

  const listOfCAProducts = listOfUSProducts.filter(
    (lp) => lp.sizeName === "mid"
  );

  listOfCAProducts.push(other);

  const getProduct = (list, v) => list.find((p) => p.name === v) || { id: "" };

  // USE EFFECT
  useEffect(() => {
    const getAppNames = async () => {
      const appNameResponse = await axios(
        `${import.meta.env.VITE_ROOT_PATH}/api/organization/applications/name`
      ).catch(() => undefined);

      if (appNameResponse && appNameResponse.data) {
        setApplicationNameList(appNameResponse.data);
      }
    };
    getAppNames();

    const getCompleteListOfProjects = async () => {
      const list = await axios(
        `${import.meta.env.VITE_ROOT_PATH}/api/partner/${ooid}/legacy/import-projects`
      ).catch(() => undefined);
      if (list && list.data) {
        setCompleteList(list.data);
      }
    };
    getCompleteListOfProjects();

    const getListOfProducts = async () => {
      const { data: products } = await axios.get(
        `${import.meta.env.VITE_ROOT_PATH}/api/products`, {params: {includeDataCloud: true}}
      );
      products.push(other);
      setListOfUSProducts(
        products.map((item) => ({
          ...item,
          key: item.id,
          label: item.name,
          value: item.name,
        }))
      );
    };
    getListOfProducts();

    setValue("projectName", projectName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // validation
  const newProjectNameValidate = {
    validate: {
      empty: (value) => trim(value).length > 0 || "App name is required",
      tooShort: (value) => trim(value).length >= 3 || "App name too short",
      tooLong: (value) => trim(value).length <= 40 || "App name too long",
      inValidCharacters: (value) =>
        !/[^A-Za-z0-9-. ]/.test(trim(value)) ||
        "Valid characters permitted are A-Z, a-z, 0-9, period (.), dash (-), and spaces",
      nameTaken: (value) => {
        const pnt = trim(value);
        const dataConnector = `${pnt.toLowerCase()} - data connector`;
        const consumerApp = `${pnt.toLowerCase()} - application`;
        return (
          !(
            applicationNameList.includes(dataConnector) ||
            applicationNameList.includes(consumerApp)
          ) || "App name is taken"
        );
      },
    },
  };

  const marketplaceLocale = {
    validate: {
      empty: (value) =>
        trim(value).length > 0 || "Marketplace Locale cannot be empty",
    },
  };

  const adpProduct = {
    validate: {
      empty: (value) => trim(value).length > 0 || "ADP Product cannot be empty",
    },
  };

  const projectEnvironment = {
    validate: {
      empty: (value) =>
        trim(value).length > 0 || "Project environment cannot be empty",
    },
  };

  const ShowError = ({ message }) => {
    return (
      <SdfAlert slot="alert" size="sm" status="error" type="inline">
        {message}
      </SdfAlert>
    );
  };

  const startAPICall = () => {
    setLoading(true);
    setError(false);
    setShowToast(false);
    setInfoMessage(null);
  };

  const closeModal = () => {
    setModalObj({ visiable: false, data: {} });
    reset({
      projectName: "",
      locale: {},
      product: {},
      type: {},
      associatedProject: {},
    });
  };

  const submitImportData = async (formData) => {
    startAPICall();
    try {
      const localValue = formData.locale.value;
      const productValue = formData.product.value;
      const typeValue = formData.type.value;

      const platform = productValue.includes("ADP® DataCloud") ? "datacloud" : "marketplace";
      setLoading(true);
      const legacyProject = {
        name: formData.projectName,
        appStore: localValue,
        size:
          localValue === "CA"
            ? getProduct(listOfCAProducts, productValue).sizeName
            : getProduct(listOfUSProducts, productValue).sizeName,
        type: typeValue,
        platform,
      };

      if (formData.associatedProject.id) {
        legacyProject.link = formData.associatedProject.id;
      }

      const isDataConnector = grantTypeCode === "client_credentials";

      // B2B (Data Connector) can't have a companion app
      if (isDataConnector) {
        legacyProject.b2bId = consumerApplicationID;
        legacyProject.b2cId = null;
      } else {
        legacyProject.b2bId = companionAppID;
        legacyProject.b2cId = consumerApplicationID;
      }

      if (typeValue === "PROD") {
        legacyProject.appListingUrls = (appListingUrls || []).reduce(
          (list, i) => {
            if (i.name && i.url) {
              list.push(i);
            }
            return list;
          },
          []
        );
      }

      const {
        data: { success },
      } = await axios.post(
        `${import.meta.env.VITE_ROOT_PATH}/api/partner/legacy/projects/import`,
        legacyProject
      );

      if (!success) {
        setError(true);
        setInfoMessage(
          "Something went wrong when trying to import the project. Please try again."
        );
      } else {
        closeModal();
        reloadProjectList();
        setInfoMessage("Successfully imported " + formData.projectName);
      }
    } catch (e) {
      setError(true);
      setInfoMessage(
        get(
          e,
          "response.data.message",
          "Something went wrong when trying to import the project. Please try again."
        )
      );
    } finally {
      setShowToast(true);
      setLoading(false);
    }
  };

  return (
    <SdfFocusPane
      visible={true}
      closeable={true}
      dismissOnClickOutside={true}
      heading={""}
      hideDismissButton={true}
      hideAcceptButton={true}
      onSdfDismiss={() => closeModal()}
      spacing={"loose"}
      id="sdf-modal"
      size={"md"}
    >
      <div className="w-full">
        <div className="text-heading-05 font-bold">Define this project</div>
        <div className="text-body-01 mt-3">
          Provide details about the project so it will be easily identifiable
          for the partner on the project dashboard upon import.
        </div>
        <div className="w-full flex flex-row flex-wrap mt-6">
          <div className="w-full mb-5 w-full">
            <Controller
              name="projectName"
              control={control}
              rules={newProjectNameValidate}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <div>
                  <SdfInput
                    name="projectName"
                    label="New project name"
                    placeholder="New project name"
                    className="w-full"
                    onSdfInput={(event) => {
                      onChange(event.detail);
                    }}
                    value={value}
                    state={error ? "error" : "normal"}
                    maxLength={255}
                  >
                    {error && error.message && (
                      <ShowError message={error.message} />
                    )}
                  </SdfInput>
                </div>
              )}
            />
          </div>
          <div className="w-full mb-5 w-1/2">
            <Controller
              name="locale"
              control={control}
              rules={marketplaceLocale}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <div className="pr-3">
                  <SdfSelect
                    name="locale"
                    label="Marketplace Locale"
                    placeholder="Select one..."
                    className="w-full"
                    items={listOfLocales}
                    onSdfChange={(event) => {
                      setSelectedValues({
                        ...selectedValues,
                        local: event.detail.value,
                      });
                      onChange(event.detail);
                      if (event.detail.value === "CA") {
                        setValue("product", listOfCAProducts[0]);
                      }
                    }}
                    value={value}
                    state={error ? "error" : "normal"}
                    maxLength={40}
                  >
                    {error && error.message && (
                      <ShowError message={error.message} />
                    )}
                  </SdfSelect>
                </div>
              )}
            />
          </div>
          <div className="w-full mb-5 w-1/2">
            <Controller
              name="product"
              control={control}
              rules={adpProduct}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <div className="pl-3">
                  <SdfSelect
                    name="product"
                    label="ADP Product"
                    placeholder="Select one..."
                    className="w-full"
                    onSdfChange={(event) => {
                      onChange(event.detail);
                    }}
                    value={value}
                    items={
                      selectedValues.local === "CA"
                        ? listOfCAProducts
                        : listOfUSProducts
                    }
                    state={error ? "error" : "normal"}
                  >
                    {error && error.message && (
                      <ShowError message={error.message} />
                    )}
                  </SdfSelect>
                </div>
              )}
            />
          </div>
          <div className="w-full mb-5 w-1/2">
            <Controller
              name="type"
              control={control}
              rules={projectEnvironment}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <div className="pr-3">
                  <SdfSelect
                    name="type"
                    label="Project environment"
                    placeholder="Select DEV or PROD"
                    className="w-full"
                    items={listOfTypes}
                    onSdfChange={(event) => {
                      setSelectedValues({
                        ...selectedValues,
                        type: event.detail.value,
                      });
                      onChange(event.detail);
                      setValue("associatedProject", {});
                    }}
                    value={value}
                    state={error ? "error" : "normal"}
                    maxLength={40}
                  >
                    {error && error.message && (
                      <ShowError message={error.message} />
                    )}
                  </SdfSelect>
                </div>
              )}
            />
          </div>
          {selectedValues.type ? (
            <div className="w-full mb-5 w-1/2">
              <Controller
                name="associatedProject"
                control={control}
                rules={projectEnvironment}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <div className="pl-3">
                    <SdfSelectSimple
                      name="associatedProject"
                      label={`Select Associated ${
                        selectedValues.type === "DEV" ? "PROD" : "DEV"
                      } project`}
                      placeholder="Select Associated project"
                      className="w-full"
                      portalEnabled={true}
                      items={
                        selectedValues.type === "PROD"
                          ? listOfDevProjects.map((item) => ({
                              ...item,
                              label: item.name,
                              value: item.name,
                            }))
                          : listOfProdProjects.map((item) => ({
                              ...item,
                              label: item.name,
                              value: item.name,
                            }))
                      }
                      onSdfChange={(event) => {
                        onChange(event.detail);
                      }}
                      value={value}
                      state={error ? "error" : "normal"}
                      maxLength={40}
                    >
                      {error && error.message && (
                        <ShowError message={error.message} />
                      )}
                    </SdfSelectSimple>
                  </div>
                )}
              />
            </div>
          ) : null}
        </div>
        <div className="mt-4">
          <SdfBox variant="filled" spacing="loose">
            <div className="w-full flex gap-4">
              <div className="text-heading-04 w-1/3">Existing app info</div>
              <div className="w-2/3">
                <div className="w-full">
                  <div className="text-body-02 font-bold">{projectName}</div>
                  <div className="text-body-01">CAR app name</div>

                  <div className="text-body-02 font-bold mt-4">
                    {consumerApplicationID}
                  </div>
                  <div className="text-body-01">Consumer app ID</div>

                  <div className="text-body-02 font-bold mt-4">{ooid}</div>
                  <div className="text-body-01">Partner OOID</div>
                </div>
                {companionProjectName && companionAppID ? (
                  <React.Fragment>
                    <div className="w-full mt-4">
                      <SdfAlert status="info" type="banner" size="sm">
                        <span>
                          The selected end-user app has a companion data
                          connector. The two apps will be imported as one legacy
                          project.
                        </span>
                        <span slot="title">Data connector companion found</span>
                      </SdfAlert>
                    </div>
                    <div className="w-full mt-4">
                      <div className="text-body-02 font-bold">
                        {companionProjectName}
                      </div>
                      <div className="text-body-01">
                        Companion data connector name
                      </div>
                      <div className="text-body-02 font-bold mt-4">
                        {companionAppID}
                      </div>
                      <div className="text-body-01">
                        Companion consumer app ID
                      </div>
                    </div>
                  </React.Fragment>
                ) : null}
              </div>
            </div>
          </SdfBox>
        </div>
      </div>

      <SdfButton slot="custom-buttons" onClick={closeModal}>
        Cancel
      </SdfButton>
      <SdfButton
        slot="custom-buttons"
        emphasis="primary"
        onClick={(e) => {
          handleSubmit((data) => submitImportData(data))();
        }}
      >
        Import
      </SdfButton>
    </SdfFocusPane>
  );
};
